import {
  invalidValidNameMessage,
  isValidName,
} from '@daymade/common-lib/validationRules';
import * as Sentry from '@sentry/browser';
import clsx from 'clsx';
import Apple from 'components/Apple';
import Button from 'components/common/Button';
import ErrorMessage from 'components/enter2026late/ErrorMessage';
import { Props as SlidingModalProps } from 'components/enter2026late/SlidingModal';
import { V2_ENTER_LAYOUT_SCROLL_POSITION_MARKER } from 'components/enter2026late/V2/V2EnterLayout';
import Facebook from 'components/Facebook';
import Google from 'components/Google';
import { useABTestStore } from 'hooks/stores/useABTestStore';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { Checkbox, Form } from 'semantic-ui-react';
import {
  getClientSideAndServerSideAbTestValues,
  importAbTestVariantsIntoLocalStorage,
} from 'utils/analytics/abtest';
import {
  identifyUser,
  trackEvent,
  trackRegistrationComplete,
} from 'utils/analytics/analytics';
import { tryApi } from 'utils/api/common';
import { authApiClient, preferencesApiClient } from 'utils/apiClient';
import { sleep } from 'utils/common';
import { APP_ENDPOINT } from 'utils/constants';
import { setCookie } from 'utils/cookie';
import HttpStatusCode from 'utils/HttpStatusCode';
import { validEmail } from 'utils/RegistrationValidator';
import { isFacebookApp } from 'utils/ua';

import { useChangeEmailAddressSlidingModal } from './ChangeEmailAddressSlidingModal';
import styles from './EnterFlowAccountForm.module.css';
import { useForgottenPasswordSlidingModal } from './ForgottenPasswordSlidingModal';

type FormMode = 'register' | 'login' | 'registerConfirmName';

interface RegisteredPlayer {
  id: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  referralId: string;
}

interface PendingSocialRegistration {
  emailAddress: string;
  googleUserId?: string;
  facebookUserId?: string;
  appleUserId?: string;
}

interface EnterFlowAccountFormProps {
  completionHandler: (token: string) => void | Promise<void>;
  appleRedirectPath: string;
  defaultMode?: FormMode;
  slidingModalSlideAmount?: SlidingModalProps['slideAmount'];
}

const EnterFlowAccountForm = observer(
  ({
    completionHandler,
    appleRedirectPath,
    slidingModalSlideAmount,
    defaultMode = 'register',
  }: EnterFlowAccountFormProps) => {
    const router = useRouter();
    const { isMobile } = useIsMobile();
    const { updateCurrentUser, currentUser } = useUserStore();
    const { displayToast } = useUIStore();
    const abTestStore = useABTestStore();
    const { referralId } = useEnterStore();

    const [mode, setMode] = useState<FormMode>(defaultMode);
    const isRegisterMode = mode === 'register';
    const isConfirmSocialMode = mode === 'registerConfirmName';
    const showRegistrationFields = isRegisterMode || isConfirmSocialMode;
    const [loggedInMethod, setLoggedInMethod] = useState<
      'NEW_ACCOUNT' | 'ALREADY_LOGGED_IN' | undefined
    >(currentUser ? 'ALREADY_LOGGED_IN' : undefined);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const [pendingSocialRegistration, setPendingSocialRegistration] = useState<
      PendingSocialRegistration | undefined
    >();

    const [confirmedAge, setConfirmedAge] = useState(false);
    const [agreedToMarketing, setAgreedToMarketing] = useState(false);
    const [agreedToMarketingStep2, setAgreedToMarketingStep2] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [hideGoogleLogin, setHideGoogleLogin] = useState(false);
    const forgottenPassword = useForgottenPasswordSlidingModal({
      slideAmount: slidingModalSlideAmount,
      onRegisterClick: () => switchMode('register'),
    });
    const [hideAppleLogin, setHideAppleLogin] = useState(false);

    useEffect(() => {
      if (!router.isReady) return;
      const result = isFacebookApp(isMobile);

      setHideGoogleLogin(result);
      setHideAppleLogin(result);
    }, [router.isReady, isMobile]);

    useEffect(() => {
      if (!loggedInMethod) {
        setLoggedInMethod('ALREADY_LOGGED_IN');
      }
    }, [!!currentUser]);

    const switchMode = (newMode: FormMode) => {
      setMode(newMode);
      setError(null);
      if (newMode !== 'registerConfirmName') {
        // Clear any pending social registration info if we're changing to a different step
        setPendingSocialRegistration(undefined);
      }
    };

    // No existing account was found for the social login, so ask the user to
    // confirm their name, age and marketing preferences before registering.
    const startSocialRegistration = ({
      firstName: socialFirstName,
      lastName: socialLastName,
      ...pending
    }: PendingSocialRegistration & { firstName: string; lastName: string }) => {
      // Ensure that the marketing preference on the second step matches what they did on the first step
      setAgreedToMarketingStep2(agreedToMarketing);
      setPendingSocialRegistration(pending);
      setFirstName(socialFirstName);
      setLastName(socialLastName);
      switchMode('registerConfirmName');
    };

    const showError = async (message: string) => {
      setError(message);

      // Scroll to the error message, once it has appear, and also take into account any sticky/fixed
      // DOM elements that we know about
      await sleep(100);
      const errorMessage = document.getElementById('enterFlowAccountFormError');
      const stickyHeaderElement = document.getElementById(
        V2_ENTER_LAYOUT_SCROLL_POSITION_MARKER,
      );

      if (errorMessage) {
        const stickyRect = stickyHeaderElement
          ? stickyHeaderElement.getBoundingClientRect()
          : { top: 0, height: 0 };
        const stickyHeaderHeight = stickyRect.top + stickyRect.height;
        const errorMessageTop =
          errorMessage.getBoundingClientRect().top + window.scrollY;

        window.scrollTo({
          top: errorMessageTop - stickyHeaderHeight,
          behavior: 'smooth',
        });
      }
    };

    const handleSuccessfulLogin = async (
      token: string,
      welcomeName: string,
      abTestVariants?: Record<string, string> | null,
    ) => {
      setCookie('jwt', token, { secure: true, expires: 30 });
      const user = await updateCurrentUser(token, async () => {
        const response = await tryApi(
          preferencesApiClient.auth.getEmailPreferences(),
        );
        if (response.status === HttpStatusCode.OK) {
          setAgreedToMarketing(response.body.agreedToMarketing);
        }
      });

      if (!user) {
        return;
      }

      trackEvent('Login');
      identifyUser({
        id: user.id,
        emailAddress: user.emailAddress,
        phoneNumber: user.phoneNumber || undefined,
        firstName: user.firstName,
        lastName: user.lastName,
      });

      displayToast({
        title: welcomeName
          ? `Welcome back to DAYMADE, ${welcomeName}!`
          : 'Welcome back to DAYMADE!',
        timeout: 3500,
      });

      if (abTestVariants) {
        importAbTestVariantsIntoLocalStorage(abTestVariants);
        abTestStore.updateABTestValuesFromLocalStorage();
      }

      await completionHandler(token);
    };

    const handleSuccessfulRegistration = async (
      token: string,
      player: RegisteredPlayer,
    ) => {
      setCookie('jwt', token, { secure: true, expires: 30 });
      await updateCurrentUser(token);

      trackRegistrationComplete(
        player.emailAddress,
        player.referralId,
        player.firstName,
        player.lastName,
      );
      identifyUser({
        id: player.id,
        emailAddress: player.emailAddress,
        phoneNumber: player.phoneNumber || undefined,
        firstName: player.firstName,
        lastName: player.lastName,
        agreedToMarketing,
      });

      displayToast({
        title: `Welcome to DAYMADE, ${player.firstName}!`,
        timeout: 3500,
      });

      await completionHandler(token);
    };

    const registerWithDetails = async ({
      emailAddress,
      firstName: registerFirstName,
      lastName: registerLastName,
      password: registerPassword,
      googleUserId,
      facebookUserId,
      appleUserId,
    }: {
      emailAddress: string;
      firstName: string;
      lastName: string;
      password: string | null;
      googleUserId?: string | null;
      facebookUserId?: string | null;
      appleUserId?: string | null;
    }) => {
      const registerRes = await authApiClient.public.register({
        body: {
          emailAddress: emailAddress.trim(),
          firstName: registerFirstName.trim(),
          lastName: registerLastName.trim(),
          password: registerPassword,
          agreedToMarketing,
          inviterId: referralId ?? null,
          phoneNumber: null,
          dateOfBirth: null,
          facebookUserId: facebookUserId ?? null,
          googleUserId: googleUserId ?? null,
          appleUserId: appleUserId ?? null,
          utmParameters: null,
          abTestVariants: getClientSideAndServerSideAbTestValues(),
        },
      });

      if (registerRes.status === HttpStatusCode.OK) {
        setError(null);
        await handleSuccessfulRegistration(
          registerRes.body.token,
          registerRes.body.player,
        );
      } else {
        let errorMessage;
        if ('reason' in registerRes.body) {
          errorMessage =
            humanReadableErrorMessages[
              registerRes.body.reason as keyof typeof humanReadableErrorMessages
            ];
        }
        if (!errorMessage) {
          errorMessage = registerRes.body.message;
        }
        await showError(errorMessage || 'Sorry, an error occurred');
      }
    };

    const loginButtonHandler = async () => {
      setError(null);
      setLoading(true);

      try {
        const res = await authApiClient.public.login({
          body: { email: email.trim(), password },
        });

        if (res.status === HttpStatusCode.OK) {
          await handleSuccessfulLogin(
            res.body.token,
            res.body.player.firstName,
            res.body.player.abtestVariants,
          );
          setLoading(false);
          return;
        }

        if (res.status === HttpStatusCode.UNAUTHORIZED) {
          await showError(res.body.message);
        }
      } catch {
        await showError(
          'Something went wrong. Please email us if this issue persists: hello@daymade.co.uk',
        );
      }

      setLoading(false);
    };

    const registerButtonHandler = async () => {
      if (!isValidName(firstName) || !isValidName(lastName)) {
        await showError(invalidValidNameMessage);
        return;
      }

      if (!validEmail(email)) {
        await showError('Please provide a valid email address.');
        return;
      }

      setLoading(true);

      try {
        await registerWithDetails({
          emailAddress: email,
          firstName,
          lastName,
          password,
        });
      } catch {
        await showError(
          'Something went wrong. Please email us if this issue persists: hello@daymade.co.uk',
        );
      }

      setLoading(false);
    };

    const confirmSocialRegistrationHandler = async () => {
      setError(null);

      if (!pendingSocialRegistration) {
        return;
      }

      if (!isValidName(firstName) || !isValidName(lastName)) {
        await showError(invalidValidNameMessage);
        return;
      }

      if (!confirmedAge) {
        await showError('You must confirm you are 18 or over to enter.');
        return;
      }

      setLoading(true);

      try {
        await registerWithDetails({
          ...pendingSocialRegistration,
          firstName,
          lastName,
          password: null,
        });
        setAgreedToMarketingStep2(agreedToMarketing);
      } catch {
        await showError(
          'Something went wrong. Please email us if this issue persists: hello@daymade.co.uk',
        );
      }

      setLoading(false);
    };

    const formSubmitHandler = (e?: React.SyntheticEvent) => {
      e?.preventDefault();
      if (isConfirmSocialMode) {
        return confirmSocialRegistrationHandler();
      }
      if (isRegisterMode) {
        return registerButtonHandler();
      }
      return loginButtonHandler();
    };

    const handleGoogleAuth = async (response: { access_token: string }) => {
      if (mode === 'login') {
        const result = await authApiClient.public.loginWithGoogle({
          body: { accessToken: response.access_token },
        });

        if (result.status === HttpStatusCode.OK) {
          await handleSuccessfulLogin(
            result.body.token,
            result.body.welcomeName,
            result.body.abtestVariants,
          );
        } else {
          await showError(result.body.message);
        }
        return;
      }

      const validateRes = await authApiClient.public.validateGoogleAccessToken({
        body: { accessToken: response.access_token },
      });

      if (validateRes.status === HttpStatusCode.OK) {
        await handleSuccessfulLogin(
          validateRes.body.token,
          validateRes.body.welcomeName,
          validateRes.body.abtestVariants,
        );
        return;
      }

      if (validateRes.status === HttpStatusCode.BAD_REQUEST) {
        await showError(validateRes.body.message);
        return;
      }

      // No existing account was found — fetch the profile so the account can be created.
      const profileRes = await fetch(
        'https://www.googleapis.com/oauth2/v3/userinfo',
        {
          headers: { Authorization: `Bearer ${response.access_token}` },
        },
      );
      const profile = await profileRes.json();

      startSocialRegistration({
        emailAddress: profile.email ?? '',
        firstName: profile.given_name ?? '',
        lastName: profile.family_name ?? '',
        googleUserId: validateRes.body.googleUserId,
      });
    };

    const handleFacebookAuth = async (response: {
      accessToken?: string;
      userID?: string;
      email: string;
      first_name?: string;
      last_name?: string;
    }) => {
      if (!response.accessToken || !response.userID) {
        Sentry.captureMessage(
          `Facebook auth failed no response.accessToken ${response.accessToken} or response.userID ${response.userID}`,
        );
        return;
      }

      if (mode === 'login') {
        const result = await authApiClient.public.loginWithFacebook({
          body: { accessToken: response.accessToken },
        });

        if (result.status === HttpStatusCode.OK) {
          await handleSuccessfulLogin(
            result.body.token,
            result.body.welcomeName,
            result.body.abtestVariants,
          );
        } else {
          await showError(result.body.message);
        }
      } else {
        const validateRes =
          await authApiClient.public.validateFacebookAccessToken({
            body: {
              accessToken: response.accessToken,
              userId: response.userID,
              email: response.email,
            },
          });

        if (validateRes.status === HttpStatusCode.OK) {
          await handleSuccessfulLogin(
            validateRes.body.token,
            validateRes.body.welcomeName,
            validateRes.body.abtestVariants,
          );
          return;
        }

        if (validateRes.status === HttpStatusCode.BAD_REQUEST) {
          await showError(validateRes.body.message);
          return;
        }

        startSocialRegistration({
          emailAddress: response.email ?? '',
          firstName: response.first_name ?? '',
          lastName: response.last_name ?? '',
          facebookUserId: validateRes.body.facebookUserId,
        });
      }
    };

    const handleAppleAuth = async (appleUser: {
      authorization: { id_token: string };
      user?: { name?: { firstName?: string; lastName?: string } };
    }) => {
      if (mode === 'login') {
        const { status, body } = await authApiClient.public.loginWithApple({
          body: { accessToken: appleUser.authorization.id_token },
        });

        if (status === HttpStatusCode.OK) {
          await handleSuccessfulLogin(
            body.token,
            body.welcomeName,
            body.abtestVariants,
          );
        } else {
          await showError(body.message);
        }
        return;
      }

      const validateRes = await authApiClient.public.validateAppleAccessToken({
        body: { accessToken: appleUser.authorization.id_token },
      });

      if (validateRes.status === HttpStatusCode.OK) {
        await handleSuccessfulLogin(
          validateRes.body.token,
          validateRes.body.welcomeName,
          validateRes.body.abtestVariants,
        );
        return;
      }

      if (validateRes.status === HttpStatusCode.BAD_REQUEST) {
        await showError(validateRes.body.message);
        return;
      }

      startSocialRegistration({
        emailAddress: validateRes.body.appleEmailAddress ?? '',
        firstName: appleUser.user?.name?.firstName ?? '',
        lastName: appleUser.user?.name?.lastName ?? '',
        appleUserId: validateRes.body.appleUserId,
      });
    };

    const loggedInMarketingChangeHandler = (value: boolean) => {
      setAgreedToMarketingStep2(value);
      (async () => {
        await preferencesApiClient.auth.setEmailPreferences({
          body: {
            agreedToMarketing: value,
          },
        });
      })();
    };

    const isContinueDisabled = isConfirmSocialMode
      ? !firstName.trim() || !lastName.trim() || !confirmedAge
      : isRegisterMode
        ? !firstName || !lastName || !email || !password || !confirmedAge
        : !email || !password;

    return (
      <div className={clsx(styles.accountForm, 'daymadeTypographySRegular')}>
        <p className={clsx(styles.eyebrow, 'daymadeTypographyTaglineAccent')}>
          Add to account
        </p>
        <h2
          className={clsx(
            styles.heading,
            'daymadeTypographyMLBlack textAlignLeft',
          )}
        >
          Access your wins
        </h2>

        {currentUser ? (
          <LoggedInDetails
            emailAddress={currentUser.emailAddress}
            hasSocialLogin={currentUser.hasSocialLogin}
            showMarketingOptIn={!agreedToMarketing}
            agreedToMarketing={agreedToMarketingStep2}
            onAgreedToMarketingChange={loggedInMarketingChangeHandler}
            slidingModalSlideAmount={slidingModalSlideAmount}
          />
        ) : (
          <>
            {isConfirmSocialMode ? (
              <p className={styles.confirmIntro}>
                <strong>Thank you!</strong> Your account was successfully
                linked. We just need to confirm your name to proceed:
              </p>
            ) : (
              <SocialButtons
                hideGoogleLogin={hideGoogleLogin}
                hideAppleLogin={hideAppleLogin}
                appleRedirectPath={appleRedirectPath}
                onGoogleSuccess={handleGoogleAuth}
                onAppleSuccess={handleAppleAuth}
                onFacebookComplete={handleFacebookAuth}
              />
            )}

            {error && (
              <ErrorMessage id="enterFlowAccountFormError">
                <strong className={styles.errorIntro}>Whoops...</strong> {error}
              </ErrorMessage>
            )}

            <Form id="enterFlowAccountForm" onSubmit={formSubmitHandler}>
              {showRegistrationFields && (
                <NameFields
                  firstName={firstName}
                  lastName={lastName}
                  onFirstNameChange={setFirstName}
                  onLastNameChange={setLastName}
                />
              )}

              {isConfirmSocialMode ? (
                <SocialRegisteredEmail
                  emailAddress={pendingSocialRegistration?.emailAddress}
                />
              ) : (
                <CredentialFields
                  isRegisterMode={isRegisterMode}
                  email={email}
                  password={password}
                  onEmailChange={(value) => {
                    setEmail(value);
                    setError(null);
                  }}
                  onPasswordChange={(value) => {
                    setPassword(value);
                    setError(null);
                  }}
                  onSwitchToLogin={() => switchMode('login')}
                  onResetPassword={() => forgottenPassword.open(email.trim())}
                />
              )}

              {showRegistrationFields && (
                <>
                  <p className={styles.checkboxesIntro}>
                    DAYMADE is only available to people aged 18 or over. Select
                    all that apply
                  </p>
                  <div className={styles.checkboxes}>
                    <Form.Field>
                      <Checkbox
                        id="ageConfirm"
                        label={
                          <label htmlFor="ageConfirm">
                            I confirm <strong>I am 18+</strong> and agree to the
                            Terms & Conditions and the Privacy Policy.
                          </label>
                        }
                        checked={confirmedAge}
                        onChange={() => setConfirmedAge(!confirmedAge)}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Checkbox
                        label="I'd love to hear about the latest promotions and news from DAYMADE"
                        checked={agreedToMarketing}
                        onChange={() =>
                          setAgreedToMarketing(!agreedToMarketing)
                        }
                      />
                    </Form.Field>
                  </div>
                </>
              )}

              <input
                type="submit"
                style={{ visibility: 'hidden', position: 'absolute' }}
              />

              <Button
                fullWidth
                onClick={formSubmitHandler}
                disabled={isContinueDisabled}
                loading={loading}
                className={styles.continueButton}
              >
                Continue
              </Button>

              {mode === 'login' && (
                <div className={styles.switchToRegister}>
                  <span>Don&apos;t have an account?</span>
                  <Button
                    buttonStyle="Tertiary"
                    buttonSize="Small"
                    className={styles.linkButton}
                    onClick={() => switchMode('register')}
                  >
                    Register
                  </Button>
                </div>
              )}
            </Form>
            {forgottenPassword.render()}
          </>
        )}
      </div>
    );
  },
);

const LoggedInDetails = observer(
  ({
    emailAddress,
    hasSocialLogin,
    showMarketingOptIn,
    agreedToMarketing,
    onAgreedToMarketingChange,
    slidingModalSlideAmount,
  }: {
    emailAddress: string;
    hasSocialLogin: boolean;
    showMarketingOptIn: boolean;
    agreedToMarketing: boolean;
    onAgreedToMarketingChange: (value: boolean) => void;
    slidingModalSlideAmount: SlidingModalProps['slideAmount'];
  }) => {
    const changeEmail = useChangeEmailAddressSlidingModal({
      slideAmount: slidingModalSlideAmount,
    });
    return (
      <div className={styles.loggedInContainer}>
        <div>
          <div className="daymadeTypographyTaglineAccent">Registered email</div>
          <div className={styles.loggedInEmailContainer}>
            {emailAddress}
            {!hasSocialLogin && (
              <>
                <Button
                  buttonSize="Small"
                  buttonStyle="Tertiary"
                  onClick={() => {
                    changeEmail.startEditing(emailAddress);
                  }}
                >
                  Change
                </Button>
                {changeEmail.render()}
              </>
            )}
          </div>
        </div>

        {showMarketingOptIn && (
          <Checkbox
            id="loggedInMarketingOptIn"
            label={
              <label htmlFor="loggedInMarketingOptIn">
                I'd love to hear about the latest promotions and news from
                DAYMADE
              </label>
            }
            checked={agreedToMarketing}
            onChange={(e) => {
              // NOTE - the currentTarget ends up being a div, rather than the input[type=checkbox]
              onAgreedToMarketingChange((e.target as HTMLInputElement).checked);
            }}
          />
        )}
      </div>
    );
  },
);

const SocialButtons = ({
  hideGoogleLogin,
  hideAppleLogin,
  appleRedirectPath,
  onGoogleSuccess,
  onAppleSuccess,
  onFacebookComplete,
}: {
  hideGoogleLogin: boolean;
  hideAppleLogin: boolean;
  appleRedirectPath: string;
  onGoogleSuccess: React.ComponentProps<typeof Google>['onSuccess'];
  onAppleSuccess: React.ComponentProps<typeof Apple>['onSuccess'];
  onFacebookComplete: React.ComponentProps<
    typeof Facebook
  >['completionHandler'];
}) => (
  <div className={styles.socialButtons}>
    {!hideGoogleLogin && (
      <Google
        enterFlow2026
        buttonText="Google"
        onSuccess={onGoogleSuccess}
        onError={async (error) => console.log('google auth onError', error)}
        onNonOAuthError={async (error) =>
          console.log('google auth NonOAuthError', error)
        }
      />
    )}
    {!hideAppleLogin && (
      <Apple
        enterFlow2026
        buttonText="Apple"
        redirectURI={`https://${APP_ENDPOINT}${appleRedirectPath}`}
        onSuccess={onAppleSuccess}
      />
    )}
    <Facebook
      enterFlow2026
      buttonText="Facebook"
      scope="email"
      completionHandler={onFacebookComplete}
    />
  </div>
);

const NameFields = ({
  firstName,
  lastName,
  onFirstNameChange,
  onLastNameChange,
}: {
  firstName: string;
  lastName: string;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
}) => (
  <Form.Group widths={2} unstackable className={styles.nameRow}>
    <Form.Field>
      <label className={styles.fieldLabel}>First name</label>
      <input
        className={styles.input}
        placeholder="John"
        type="text"
        autoComplete="given-name"
        value={firstName}
        onChange={(e) => onFirstNameChange(e.target.value)}
      />
    </Form.Field>
    <Form.Field>
      <label className={styles.fieldLabel}>Last name</label>
      <input
        className={styles.input}
        placeholder="Smith"
        type="text"
        autoComplete="family-name"
        value={lastName}
        onChange={(e) => onLastNameChange(e.target.value)}
      />
    </Form.Field>
  </Form.Group>
);

const SocialRegisteredEmail = ({
  emailAddress,
}: {
  emailAddress: string | undefined;
}) => (
  <>
    <div
      className={clsx(
        'daymadeTypographyTaglineAccent',
        styles.registeredEmailText,
      )}
    >
      Registered email
    </div>
    <div className={styles.loggedInEmailContainer}>{emailAddress}</div>
  </>
);

const CredentialFields = ({
  isRegisterMode,
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSwitchToLogin,
  onResetPassword,
}: {
  isRegisterMode: boolean;
  email: string;
  password: string;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSwitchToLogin: () => void;
  onResetPassword: () => void;
}) => (
  <>
    <Form.Field>
      <label className={styles.fieldLabel}>Email address</label>
      <input
        className={styles.input}
        placeholder="name@example.com"
        type="email"
        autoComplete="username"
        autoCorrect="off"
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
      />
    </Form.Field>

    <Form.Field>
      <label className={styles.fieldLabel}>
        {isRegisterMode ? 'Create your password' : 'Password'}
      </label>
      <input
        className={styles.input}
        placeholder="Password (min. 8 chars)"
        type="password"
        autoComplete={isRegisterMode ? 'new-password' : 'current-password'}
        value={password}
        onChange={(e) => onPasswordChange(e.target.value)}
      />
    </Form.Field>

    <div className={styles.authSwitchRow}>
      {isRegisterMode ? (
        <>
          <span>Already have an account?</span>
          <Button
            key="login"
            buttonStyle="Tertiary"
            buttonSize="Small"
            onClick={onSwitchToLogin}
          >
            Login
          </Button>
        </>
      ) : (
        <>
          <span>Forgotten your password?</span>
          <Button
            // This needs a key, otherwise React will use the same DOM element as the login link, which breaks the anchor border animation
            key="reset"
            buttonStyle="Tertiary"
            buttonSize="Small"
            onClick={onResetPassword}
          >
            Reset Password
          </Button>
        </>
      )}
    </div>
  </>
);

const humanReadableErrorMessages = {
  EMAIL_ADDRESS_ALREADY_REGISTERED:
    'Sorry, this email address is already registered. Login or reset your password.',
  EMAIL_ADDRESS_NOT_VALID:
    'Sorry, this email address is not valid. Please use another email address',
} as const;

export default EnterFlowAccountForm;
